import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import usePageTitle from '../hooks/usePageTitle';

const PrivacyPolicy: React.FC = () => {
  usePageTitle('Privacy Policy');

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Button variant="ghost" className="mb-6" asChild>
          <Link to="/my-apps">
            <ChevronLeft aria-hidden="true" />
            Back to Portal
          </Link>
        </Button>

        <h1 className="text-2xl font-semibold mb-2">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mb-8">Effective Date: April 1, 2026</p>

        <div className="space-y-6 text-sm leading-relaxed">
          <section>
            <h2 className="text-base font-semibold mb-2">1. Introduction</h2>
            <p>The <strong>Institute for Sensing and Embedded Network Systems Engineering (I-SENSE)</strong> at <strong>Florida Atlantic University (FAU)</strong> is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Dory Portal ("the Service"). This policy is intended to comply with applicable Florida law, FAU data governance policies, and federal regulations including FERPA where applicable.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">2. Information We Collect</h2>
            <h3 className="font-medium mb-1">Information You Provide</h3>
            <ul className="list-disc pl-6 space-y-1 mb-3">
              <li><strong>Account Information:</strong> Name, email address, and credentials used during registration and login (managed via FAU-compatible identity services).</li>
              <li><strong>Organization Data:</strong> Organization names, roles, and membership information you create or manage within the Service.</li>
              <li><strong>Research and Usage Data:</strong> Configuration settings, app definitions, sensor assignments, processor templates, and other content you submit through the Service in the course of research activities.</li>
            </ul>
            <h3 className="font-medium mb-1">Information Collected Automatically</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Log Data:</strong> IP addresses, browser type, pages visited, and timestamps when you access the Service.</li>
              <li><strong>Session Information:</strong> Authentication tokens and session identifiers used to maintain your logged-in state.</li>
              <li><strong>Device Information:</strong> Browser version and operating system, collected for compatibility and security purposes.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">3. How We Use Your Information</h2>
            <p className="mb-2">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide, operate, and maintain the Service in support of I-SENSE research activities.</li>
              <li>Authenticate your identity and manage your access permissions.</li>
              <li>Process and fulfill requests you make through the Service.</li>
              <li>Communicate with you about account activity, security alerts, and service updates.</li>
              <li>Support research operations, analysis, and platform improvements.</li>
              <li>Comply with FAU policies, applicable laws, and legal obligations.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">4. Sharing of Information</h2>
            <p className="mb-2">We do not sell, trade, or rent your personal information. We may share information in the following limited circumstances:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Service Providers:</strong> With trusted third-party vendors who assist in operating the Service (e.g., cloud infrastructure, authentication), under confidentiality obligations.</li>
              <li><strong>FAU Administration:</strong> As required by Florida Atlantic University policies or oversight procedures.</li>
              <li><strong>Legal Requirements:</strong> When required by law, regulation, or legal process, including Florida's Government-in-the-Sunshine Law (Public Records Act), or to protect the rights and safety of users.</li>
              <li><strong>Research Collaborators:</strong> De-identified or aggregated data may be shared with authorized research collaborators under applicable data use agreements.</li>
            </ul>
            <p className="mt-2 text-muted-foreground text-xs">Note: As a Florida state university, FAU is subject to Florida's Public Records Act (Chapter 119, Florida Statutes). Certain records may be subject to public records requests.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">5. Data Retention</h2>
            <p>We retain your information for as long as your account is active, as needed to provide the Service, or as required by FAU records retention schedules and applicable law. You may request deletion of your account and associated data by contacting us. We will comply to the extent permitted by applicable law and university retention requirements.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">6. Security</h2>
            <p>We implement industry-standard security measures to protect your information, including encryption in transit (TLS) and at rest, role-based access controls, and authentication via AWS Cognito. The Service operates within infrastructure consistent with FAU's information security standards. However, no method of transmission or storage is 100% secure.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">7. Cookies and Similar Technologies</h2>
            <p>The Service uses session cookies strictly necessary for authentication and maintaining your logged-in state. These cookies are removed when you sign out. We do not use tracking cookies or third-party advertising cookies.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">8. Your Rights</h2>
            <p className="mb-2">Depending on your relationship with FAU and applicable law, you may have rights to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access the personal data we hold about you.</li>
              <li>Request correction of inaccurate data.</li>
              <li>Request deletion of your personal data (subject to retention obligations).</li>
              <li>Object to or restrict certain processing of your data.</li>
            </ul>
            <p className="mt-2">To exercise any of these rights, please contact us at <a href="mailto:i-sense@fau.edu" className="text-primary underline underline-offset-4">i-sense@fau.edu</a>. FAU's broader privacy practices are described at <a href="https://www.fau.edu/privacy" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">fau.edu/privacy</a>.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">9. Children's Privacy</h2>
            <p>The Service is not directed at individuals under the age of 18 and is intended for use by researchers, faculty, and authorized professional collaborators only. We do not knowingly collect personal information from minors.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">10. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time consistent with changes to FAU policies or applicable law. We will notify you of significant changes by posting the updated policy within the Service. Your continued use of the Service after such changes constitutes your acceptance of the updated policy.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold mb-2">11. Contact</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact:</p>
            <address className="not-italic mt-2 text-muted-foreground">
              Institute for Sensing and Embedded Network Systems Engineering (I-SENSE)<br />
              Florida Atlantic University<br />
              777 Glades Road<br />
              Boca Raton, FL 33431<br />
              <a href="mailto:i-sense@fau.edu" className="text-primary underline underline-offset-4">i-sense@fau.edu</a>
            </address>
            <p className="mt-3">For broader FAU privacy matters, you may also refer to the <a href="https://www.fau.edu/regulations-policies/" target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-4">FAU Regulations &amp; Policies</a> page.</p>
          </section>
        </div>

        <div className="mt-10 border-t pt-6 text-xs text-muted-foreground">
          <Link to="/legal/terms-of-service" className="hover:text-foreground transition-colors">Terms of Service</Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
